import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Divider } from "@mui/material";
import HistogramAggSelector from "components/widgetParts/HistogramAggSelector";
import { QueryField, QueryFieldTypes, QueryInfo, socialUniverseField } from "system/query/QueryInfo";
import { Agg } from "system/query/Aggs";
import BarChartPreview from "./BarChartPreview";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest = {
  name: 'bar-chart',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultBarChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundTimestampField = queryInfo.fields?.find((field: QueryField) => field.type === QueryFieldTypes.timestamp) || false;
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
  if (!foundTimestampField) {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  requestInitBody.aggs = {
    histogram: {
      op: "histogram",
      field: foundTimestampField.id,
      ...(foundSocialUniverseField && {
        aggs: {
          [socialUniverseField.label]: {
            op: socialUniverseField.id
          }
        }
      }),
    }
  };

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export type BarChartSettingsStateType = {
  yAxis: true;
  xAxis: true;
  showLegend: false;
  showGrid: false;
  widgetTitle: string;
  customWidgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: BarChartSettingsStateType = {
  yAxis: true,
  xAxis: true,
  showLegend: false,
  showGrid: false,
  widgetTitle: 'default',
  customWidgetTitle: '',
  widgetTitleAlignment: 'left'
};

export default function BarChartSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'bar-chart');
  const requestBody = originalRequest?.requestInit?.body || {};

  const [localAggs, setLocalAggs] = useState<any>(requestBody?.aggs);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const updatedDefaultSettings = { ...defaultSettings, ...props?.widget?.data?.settings };

  const [settingsState, setSettingsState] = useState<any>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: BarChartSettingsStateType) => setSettingsState(updatedState);

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: localAggs };
    widgetRequest.requestInit = { body: requestInitBody };

    props.saveWidgetDataAndRequests({
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })

  }

  return (
    <TabbedDialog
      title={i18n.t("widget-types.bar-chart.settings.title")}
      tabs={[`${i18n.t("widgets.settings.query-tab")}`, `${i18n.t("widgets.settings.display-tab")}`]}
      open={true}
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false) }
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}>
      <>
        {/* QUERY & SETTINGS */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <HistogramAggSelector
              aggs={localAggs?.histogram}
              queryInfo={props.queryInfo}
              allowKeywordAgg={true}
              disableKeywordFilter={true}
              includeTimeOfDayAndDayOfWeekOptions={true}
              additionalLeafAggProps={{
                disableKeywordFilter: true
              }}
              setAggs={(aggs:Agg) => {
                setLocalAggs({histogram: aggs});
              }}
            />
          </>)}
        </>)}
        {/* DISPLAY SETTINGS */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            <BarChartPreview settingsState={settingsState} />
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={`${i18n.t("widget-settings.widget-title")}`} showBottomDivider={true}>
              <SettingsWidgetTitleSelect
                keyid="widgetTitle"
                label={`${i18n.t("widget-settings.widget-title")}`}
                showQueryOption={true}
                widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={`${i18n.t("widget-settings.custom-text")}`}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
              <SettingsToggleButton
                keyid="widgetTitleAlignment"
                label={i18n.t("widget-settings.alignment")}
                optionsList={horizontalAlignmentOptions}
                value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsAccordion label={`${i18n.t("graph")}`}>
                <SettingsCheckbox
                  keyid="yAxis"
                  label={i18n.t("widget-settings.show-y-axis")}
                  checked={settingsState.yAxis}/>
                <SettingsCheckbox
                  keyid="xAxis"
                  label={i18n.t("widget-settings.show-x-axis")}
                  checked={settingsState.xAxis}/>
                <SettingsCheckbox
                  keyid="showLegend"
                  label={i18n.t("widget-settings.show-legend")}
                  checked={settingsState.showLegend}/>
                <SettingsCheckbox
                  keyid="showGrid"
                  label={i18n.t("widget-settings.show-grid")}
                  checked={settingsState.showGrid}/>
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  );
}
